import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  FileDownload as ExportIcon,
  LocationCity as CityIcon,
  Refresh as RefreshIcon,
  TableRows as RowsIcon,
  Warehouse as HubIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import { vehicleService, VehicleStatusBackupLedger } from '../services/vehicleService';
import { cityService, hubService, City, HubResponse } from '../services/hubService';
const toInputDate = (date: Date) => date.toISOString().slice(0, 10);

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatHeaderDate = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const SL_NO_COLUMN_WIDTH = 92;
const PARAMETER_COLUMN_WIDTH = 420;
const DATE_COLUMN_WIDTH = 156;

type ReportMode = 'backup' | 'daily';

const toFileSafeText = (value: string) => value.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();

const VehicleStatusBackupReport: React.FC = () => {
  const today = useMemo(() => new Date(), []);
  const [reportMode, setReportMode] = useState<ReportMode>('backup');
  const [range, setRange] = useState('7');
  const [startDate, setStartDate] = useState(toInputDate(addDays(today, -6)));
  const [endDate, setEndDate] = useState(toInputDate(today));
  const [dailyDate, setDailyDate] = useState(toInputDate(today));
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedHubId, setSelectedHubId] = useState('');
  const [cities, setCities] = useState<City[]>([]);
  const [hubs, setHubs] = useState<HubResponse[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [ledger, setLedger] = useState<VehicleStatusBackupLedger | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const todayKey = toInputDate(today);
  const effectiveStartDate = reportMode === 'daily' ? dailyDate : startDate;
  const effectiveEndDate = reportMode === 'daily' ? dailyDate : endDate;

  const selectedCity = useMemo(
    () => cities.find((city) => city.id === selectedCityId),
    [cities, selectedCityId]
  );

  const selectedHub = useMemo(
    () => hubs.find((hub) => hub.id === selectedHubId),
    [hubs, selectedHubId]
  );

  const reportScopeLabel = selectedHub
    ? `${selectedHub.name} (${selectedHub.code})`
    : selectedCity
      ? selectedCity.displayName || selectedCity.name
      : 'All cities and hubs';

  const dailyRows = useMemo(() => {
    if (!ledger) return [];

    return ledger.dailyReport?.[0]?.rows || [];
  }, [ledger]);

  const loadLedger = useCallback(async () => {
    try {
      setLoading(true);
      const response = await vehicleService.getStatusBackupLedger(effectiveStartDate, effectiveEndDate, {
        cityId: selectedCityId || undefined,
        hubId: selectedHubId || undefined,
      });
      setLedger(response.data);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Failed to load status backup',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [effectiveStartDate, effectiveEndDate, selectedCityId, selectedHubId]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  useEffect(() => {
    const loadCities = async () => {
      try {
        setLocationsLoading(true);
        const citiesResponse = await cityService.getOperationalCities();
        setCities(citiesResponse);
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || 'Failed to load cities',
          severity: 'error',
        });
      } finally {
        setLocationsLoading(false);
      }
    };

    loadCities();
  }, []);

  useEffect(() => {
    const loadHubs = async () => {
      try {
        setLocationsLoading(true);
        const hubsResponse = await hubService.getOperationalHubs(selectedCityId || undefined);
        setHubs(hubsResponse);
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || 'Failed to load hubs',
          severity: 'error',
        });
      } finally {
        setLocationsLoading(false);
      }
    };

    loadHubs();
  }, [selectedCityId]);

  const handleRangeChange = (value: string) => {
    setRange(value);
    if (value === 'custom') return;

    const days = Number(value);
    const end = new Date();
    setEndDate(toInputDate(end));
    setStartDate(toInputDate(addDays(end, -(days - 1))));
  };

  const handleCityChange = (cityId: string) => {
    setSelectedCityId(cityId);
    setSelectedHubId('');
  };

  const handleExport = () => {
    if (!ledger) {
      setSnackbar({
        open: true,
        message: 'No report data available to export',
        severity: 'info',
      });
      return;
    }

    const workbook = XLSX.utils.book_new();
    const scope = reportScopeLabel;

    const backupRows = ledger.rows.map((row) => ({
      'SL No': row.slNo,
      Parameter: row.parameter,
      ...ledger.dates.reduce<Record<string, number>>((acc, date) => {
        acc[formatHeaderDate(date)] = row.values[date] ?? 0;
        return acc;
      }, {}),
    }));

    const dailyReportRows = dailyRows.map((row) => ({
      'SL No': row.slNo,
      Parameter: row.parameter,
      [formatHeaderDate(effectiveStartDate)]: row.value,
    }));

    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(backupRows),
      'Backup Report'
    );
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(dailyReportRows),
      'Daily Report'
    );
    XLSX.writeFile(
      workbook,
      `vehicle-reports-${toFileSafeText(scope)}-${effectiveStartDate}-to-${effectiveEndDate}.xlsx`
    );
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      <Paper
        elevation={0}
        sx={(theme) => ({
          mb: 3,
          overflow: 'hidden',
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.35 : 0.7)}`,
          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.72 : 0.86),
          backdropFilter: 'blur(18px)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? `0 20px 50px ${alpha(theme.palette.common.black, 0.35)}`
              : `0 20px 50px ${alpha(theme.palette.grey[700], 0.16)}`,
        })}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
          spacing={2}
          sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 }, pb: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" fontWeight={900} sx={{ fontSize: { xs: '1.6rem', md: '2.15rem' }, lineHeight: 1.05 }}>
              Vehicle Daily Status Backup
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
              <Chip
                size="small"
                icon={<CalendarIcon />}
                label={
                  reportMode === 'daily'
                    ? `Report: ${formatHeaderDate(dailyDate)}`
                    : `${formatHeaderDate(startDate)} - ${formatHeaderDate(endDate)}`
                }
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                icon={<RowsIcon />}
                label={`${ledger?.rows.length || 0} parameters`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                icon={selectedHub ? <HubIcon /> : <CityIcon />}
                label={reportScopeLabel}
                color={selectedCityId || selectedHubId ? 'primary' : 'default'}
                sx={{ fontWeight: 700, maxWidth: { xs: '100%', sm: 360 } }}
              />
            </Stack>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', lg: 'auto' } }}>
            <ToggleButtonGroup
              exclusive
              value={reportMode}
              onChange={(_, value: ReportMode | null) => {
                if (value) setReportMode(value);
              }}
              sx={(theme) => ({
                alignSelf: { xs: 'stretch', sm: 'center' },
                bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.35 : 0.55),
                border: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
                borderRadius: 2,
                p: 0.35,
                '& .MuiToggleButton-root': {
                  minHeight: 36,
                  px: 1.6,
                  border: 0,
                  borderRadius: 1.5,
                  fontWeight: 800,
                  textTransform: 'none',
                  color: 'text.secondary',
                },
                '& .Mui-selected': {
                  bgcolor: `${theme.palette.primary.main} !important`,
                  color: `${theme.palette.primary.contrastText} !important`,
                  boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                },
              })}
            >
              <ToggleButton value="backup">Backup Report</ToggleButton>
              <ToggleButton value="daily">Daily Report</ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={loading || !ledger}
              sx={(theme) => ({
                minHeight: 42,
                px: 2.25,
                borderRadius: 2,
                fontWeight: 800,
                width: { xs: '100%', sm: 'auto' },
                color: theme.palette.success.main,
                borderColor: alpha(theme.palette.success.main, 0.45),
                '&:hover': {
                  borderColor: theme.palette.success.main,
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                },
              })}
            >
              Export
            </Button>

            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={loadLedger}
              disabled={loading}
              sx={{
                minHeight: 42,
                px: 2.25,
                borderRadius: 2,
                fontWeight: 800,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={(theme) => ({
            px: { xs: 2, md: 3 },
            py: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
            bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.2 : 0.36),
          })}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            useFlexGap
            flexWrap="wrap"
            alignItems={{ md: 'center' }}
            sx={{
              '& .MuiFormControl-root, & .MuiTextField-root': {
                width: { xs: '100%', sm: 'calc(50% - 6px)', md: 'auto' },
                minWidth: { md: 170 },
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
              },
            }}
          >
            {reportMode === 'backup' ? (
              <>
                <FormControl>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={range}
                    label="Date Range"
                    onChange={(event) => handleRangeChange(event.target.value)}
                  >
                    <MenuItem value="1">Today</MenuItem>
                    <MenuItem value="7">Last 7 Days</MenuItem>
                    <MenuItem value="15">Last 15 Days</MenuItem>
                    <MenuItem value="30">Last 30 Days</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(event) => {
                    setRange('custom');
                    setStartDate(event.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: endDate }}
                />

                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(event) => {
                    setRange('custom');
                    setEndDate(event.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: startDate }}
                />
              </>
            ) : (
              <TextField
                label="Report Date"
                type="date"
                value={dailyDate}
                onChange={(event) => setDailyDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: todayKey }}
              />
            )}

            <FormControl disabled={locationsLoading}>
              <InputLabel>City</InputLabel>
              <Select
                value={selectedCityId}
                label="City"
                onChange={(event) => handleCityChange(event.target.value)}
              >
                <MenuItem value="">All Cities</MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.displayName || city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl disabled={locationsLoading} sx={{ minWidth: { md: 220 } }}>
              <InputLabel>Hub</InputLabel>
              <Select
                value={selectedHubId}
                label="Hub"
                onChange={(event) => setSelectedHubId(event.target.value)}
              >
                <MenuItem value="">All Hubs</MenuItem>
                {hubs.map((hub) => (
                  <MenuItem key={hub.id} value={hub.id}>
                    {hub.name} ({hub.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: 42,
                px: 1.5,
                borderRadius: 2,
                color: 'text.secondary',
                border: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
                bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.55 : 0.8),
              })}
            >
              <CalendarIcon fontSize="small" />
              <Typography variant="body2" fontWeight={700}>
                {reportMode === 'daily' ? formatHeaderDate(dailyDate) : `${ledger?.dates.length || 0} date columns`}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {reportMode === 'daily' && (
        <Paper
          elevation={0}
          sx={(theme) => ({
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.82 : 0.92),
            border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.35 : 0.75)}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 18px 45px ${alpha(theme.palette.common.black, 0.28)}`
                : `0 18px 45px ${alpha(theme.palette.grey[700], 0.12)}`,
          })}
        >
          <Box
            sx={(theme) => ({
              px: { xs: 2, md: 3 },
              py: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1.5,
              flexDirection: { xs: 'column', sm: 'row' },
            })}
          >
            <Box>
              <Typography variant="h6" fontWeight={900}>
                Daily Deployment Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vehicle status movement counts for the selected date and location.
              </Typography>
            </Box>
            <Chip
              size="small"
              icon={<HubIcon />}
              label={reportScopeLabel}
              color={selectedCityId || selectedHubId ? 'primary' : 'default'}
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900, width: 100 }}>SL NO</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>PARAMETER</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 900, width: 180 }}>
                    {formatHeaderDate(effectiveStartDate)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && dailyRows.map((row) => (
                  <TableRow key={row.parameter} hover>
                    <TableCell sx={{ fontWeight: 800 }}>{row.slNo}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{row.parameter}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 900, color: row.value > 0 ? 'primary.main' : 'text.primary' }}>
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {reportMode === 'backup' && (
        <Paper
        elevation={0}
        sx={(theme) => ({
          overflow: 'hidden',
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.82 : 0.9),
          border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.35 : 0.75)}`,
          backdropFilter: 'blur(14px)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? `0 18px 45px ${alpha(theme.palette.common.black, 0.3)}`
              : `0 18px 45px ${alpha(theme.palette.grey[700], 0.14)}`,
        })}
      >
        <Box
          sx={(theme) => ({
            px: { xs: 2, md: 3 },
            py: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            flexDirection: { xs: 'column', sm: 'row' },
          })}
        >
          <Box>
            <Typography variant="h6" fontWeight={900}>
              Parameters Ledger
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Operational status counts by backup date and selected location.
            </Typography>
          </Box>
          <Chip
            size="small"
            icon={<RowsIcon />}
            label={`${ledger?.rows.length || 0} rows`}
            variant="outlined"
            sx={{ fontWeight: 800 }}
          />
        </Box>

        <TableContainer
          sx={{
            maxHeight: '70vh',
            overflow: 'auto',
            position: 'relative',
            bgcolor: 'background.paper',
            isolation: 'isolate',
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: `${SL_NO_COLUMN_WIDTH + PARAMETER_COLUMN_WIDTH + Math.max(ledger?.dates.length || 1, 1) * DATE_COLUMN_WIDTH}px`,
              tableLayout: 'fixed',
              borderCollapse: 'separate',
              borderSpacing: 0,

              '& .MuiTableCell-root': {
                backgroundClip: 'border-box',
                borderColor: 'divider',
                py: 1.25,
              },
              '& tbody .MuiTableCell-root': {
                backgroundColor: (theme) => theme.palette.background.paper,
              },
              '& tbody tr:hover .MuiTableCell-root': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
              },
              '& .ledger-sticky-cell': {
                backgroundColor: (theme) => theme.palette.background.paper,
                backgroundImage: 'none',
              },
              '& tbody tr:hover .ledger-sticky-cell': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
                backgroundImage: 'none',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  className="ledger-sticky-cell"
                  sx={{
                    fontWeight: 700,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    position: { xs: 'static', md: 'sticky' },
                    top: { xs: 'auto', md: 0 },
                    left: { xs: 'auto', md: 0 },
                    minWidth: SL_NO_COLUMN_WIDTH,
                    width: SL_NO_COLUMN_WIDTH,
                    bgcolor: 'background.paper',
                    zIndex: { xs: 'auto', md: 7 },
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                >
                  SL NO
                </TableCell>

                <TableCell
                  className="ledger-sticky-cell"
                  sx={{
                    fontWeight: 700,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    position: { xs: 'static', md: 'sticky' },
                    top: { xs: 'auto', md: 0 },
                    left: { xs: 'auto', md: SL_NO_COLUMN_WIDTH },
                    minWidth: PARAMETER_COLUMN_WIDTH,
                    width: PARAMETER_COLUMN_WIDTH,
                    bgcolor: 'background.paper',
                    zIndex: { xs: 'auto', md: 7 },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  PARAMETER
                </TableCell>

                {ledger?.dates.map((date) => (
                  <TableCell
                    key={date}
                    align="center"
                    sx={{
                      minWidth: DATE_COLUMN_WIDTH,
                      width: DATE_COLUMN_WIDTH,
                      fontWeight: 800,
                      position: { xs: 'static', md: 'sticky' },
                      top: { xs: 'auto', md: 0 },
                      bgcolor: 'background.paper',
                      zIndex: { xs: 'auto', md: 4 },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{formatHeaderDate(date)}</Box>
                    <Typography variant="caption" color="text.secondary">
                      BACKUP
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={(ledger?.dates.length || 0) + 2} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {!loading && ledger?.rows.map((row) => (
                <TableRow key={row.parameter} hover>
                  <TableCell
                    className="ledger-sticky-cell"
                    sx={{
                      fontWeight: 700,
                      position: { xs: 'static', md: 'sticky' },
                      left: { xs: 'auto', md: 0 },
                      zIndex: { xs: 'auto', md: 5 },
                      borderRight: '1px solid #e0e0e0',
                      minWidth: SL_NO_COLUMN_WIDTH,
                      width: SL_NO_COLUMN_WIDTH,
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                    }}
                  >
                    {row.slNo}
                  </TableCell>

                  <TableCell
                    className="ledger-sticky-cell"
                    sx={{
                      fontWeight: 700,
                      position: { xs: 'static', md: 'sticky' },
                      left: { xs: 'auto', md: SL_NO_COLUMN_WIDTH },
                      zIndex: { xs: 'auto', md: 5 },
                      borderRight: '1px solid #e0e0e0',
                      minWidth: PARAMETER_COLUMN_WIDTH,
                      width: PARAMETER_COLUMN_WIDTH,
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                      lineHeight: 1.25,
                      boxShadow: { xs: 'none', md: '6px 0 12px rgba(0,0,0,0.08)' },
                    }}
                  >
                    {row.parameter}
                  </TableCell>

                  {ledger.dates.map((date) => (
                    <TableCell
                      key={`${row.parameter}-${date}`}
                      align="center"
                      sx={{ fontWeight: 700, minWidth: DATE_COLUMN_WIDTH, width: DATE_COLUMN_WIDTH }}
                    >
                      {row.values[date] ?? 0}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </Paper>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehicleStatusBackupReport;

